import { useEffect, useMemo, useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  createMapEdge,
  edgeTypeLabel,
  MAP_EDGE_TYPES,
  type MapEdgeRow,
  type MapEdgeType,
  type MapNodeRow,
} from '../../lib/systemMap';

export interface SystemMapAddConnectorDialogProps {
  open: boolean;
  mapId: string;
  nodes: MapNodeRow[];
  initialSourceId?: string | null;
  initialTargetId?: string | null;
  onClose: () => void;
  onCreated: (edge: MapEdgeRow) => void;
}

export default function SystemMapAddConnectorDialog({
  open,
  mapId,
  nodes,
  initialSourceId,
  initialTargetId,
  onClose,
  onCreated,
}: SystemMapAddConnectorDialogProps) {
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [edgeType, setEdgeType] = useState<MapEdgeType>('data');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const sortedNodes = useMemo(
    () => [...nodes].sort((a, b) => a.label.localeCompare(b.label)),
    [nodes],
  );

  useEffect(() => {
    if (!open) return;
    setSourceId(initialSourceId ?? '');
    setTargetId(initialTargetId ?? '');
    setEdgeType('data');
    setLabel('');
  }, [open, initialSourceId, initialTargetId]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!sourceId || !targetId) {
      toast.error('Choose source and target nodes');
      return;
    }
    if (sourceId === targetId) {
      toast.error('Source and target must be different nodes');
      return;
    }

    setSaving(true);
    try {
      const edge = await createMapEdge({
        mapId,
        sourceNodeId: sourceId,
        targetNodeId: targetId,
        edgeType,
        label: label.trim() || null,
      });
      onCreated(edge);
      toast.success('Connector added');
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to add connector');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/40">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-connector-title"
        className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl"
      >
        <form onSubmit={(e) => void handleSubmit(e)} className="p-5 space-y-4">
          <div>
            <h2 id="add-connector-title" className="text-base font-semibold text-gray-900 dark:text-white">
              Add connector
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Link one node to another. Drag route and anchors after creating.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              From
            </label>
            <select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Select source…</option>
              {sortedNodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              To
            </label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Select target…</option>
              {sortedNodes.map((node) => (
                <option key={node.id} value={node.id} disabled={node.id === sourceId}>
                  {node.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Type
            </label>
            <select
              value={edgeType}
              onChange={(e) => setEdgeType(e.target.value as MapEdgeType)}
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {MAP_EDGE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {edgeTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Label (optional)
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Subscribe CTA"
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Add connector
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
